/**
 * Cookie flags that let the storefront keep identity when a merchant injects
 * the shop in a cross-origin iframe.
 *
 * Nested documents are a third-party context: `SameSite=Lax` cookies are
 * neither sent nor reliably stored there. `None` + `Secure` is required.
 * Browsers reject `None` on http, so local dev stays `Lax`.
 */
export type StorefrontCookieAttrs = {
  sameSite: "none" | "lax";
  secure: boolean;
};

export const storefrontCookieAttrs = (
  secure: boolean,
): StorefrontCookieAttrs =>
  secure
    ? { sameSite: "none", secure: true }
    : { sameSite: "lax", secure: false };

/**
 * The same flags for cookies written by BROWSER code (`document.cookie`,
 * cookies-next). Deletes need them too: inside a cross-site frame the browser
 * rejects a Lax write, and a delete is just a write with an expired max-age —
 * so a bare `deleteCookie` silently leaves the cookie in place (e.g. logout).
 */
export const browserCookieAttrs = (): StorefrontCookieAttrs =>
  storefrontCookieAttrs(
    typeof location !== "undefined" && location.protocol === "https:",
  );

/** `browserCookieAttrs` as a raw `document.cookie` suffix. */
export const browserCookieAttrsString = (): string =>
  browserCookieAttrs().secure ? "; samesite=none; secure" : "; samesite=lax";
